/* The counterparty's statuses — the maps and the pills the card (`Page.js`) and the list
   (`List.js`) both draw, so the two cannot drift.

   The counterparty's OWN states (lifecycle, sync) are the filled status pill spec/02 asks
   for — a tint plus a `-700`-grade label. (A document's status in the card's Orders / Returns tabs
   is the full lists' status icon, `OrdersTab.js` / `ReturnsTab.js`, not a pill.)

   ⚠ No stylesheet import here, deliberately: the filled pill's shape is the global `.status-tag`
   (`theme/antd.scss`), and the page-scoped classes (`cp-status--*`) live in
   `counterparties.scss`, which both consumers already import. A shared module that imported it
   again — or a list that imported `Page.js` to reach these — is the Parcel re-emit trap
   `list-page.scss` records. */

import React from "react";
import { Flex, Tag, Tooltip } from "antd";
import { UserSwitchOutlined } from "@ant-design/icons";

import { COUNTERPARTIES_PAGE } from "../../config/lang";
import { translate } from "../../util/util";

/* TextChoices KEYS, in the order the API declares them. Labels are `lc_<key>`. */
export const LIFECYCLE_STATUSES = ["new_client", "onboarding", "regular", "churned"];
/* Status key → its label key in `COUNTERPARTIES_PAGE`: the counterparty's own lifecycle and sync —
   the pills `statusTag` draws. (The Orders / Returns tabs label a document's status
   with `STATUS_ICON`, not here.) */
const STATUS_LABEL = {
  new_client: "lc_new_client",
  onboarding: "lc_onboarding",
  regular: "lc_regular",
  churned: "lc_churned",
  synced: "sync_synced",
  pending: "sync_pending",
  failed: "sync_failed",
};

/* Tones of the filled pill. `ok` / `no` / `wait` are the global `.status-tag` tones; `info` (blue)
   and `off` (neutral) are this page's own — see `counterparties.scss` for why `off` is not the
   global one. A regular client is `info`, not `ok`: green is the sync pill's "synced" beside it, so
   the two pills never say the same colour for two different facts. */
const LIFECYCLE_TONE = { new_client: "off", onboarding: "wait", regular: "info", churned: "no" };

const GLOBAL_TONES = ["ok", "no", "wait"];
const label = (key, lang) => (STATUS_LABEL[key] ? translate(COUNTERPARTIES_PAGE, STATUS_LABEL[key], lang) : key || "—");

/** The filled status pill, for the counterparty's own states. `label` / `title` override the mapped
    label and add a hover title (the card's sync pill carries its time); a key missing from `tones`
    takes `fallback` — neutral. The label text states the status, so colour only reinforces it.
    `icon` is an optional leading glyph (the Contacts tab's star on the primary contact).
    ⚠ One tone class only, never the global AND the page one together: `.status-tag--off` and
    `.cp-status--off` tie on specificity, and which wins would be bundle order. */
export function statusTag(tones, key, lang, { fallback = "off", label: text, title, icon } = {}) {
  const tone = tones[key] || fallback;
  const toneClass = GLOBAL_TONES.includes(tone) ? `status-tag--${tone}` : `cp-status--${tone}`;
  return (
    <Tag variant="filled" className={`status-tag ${toneClass}`} title={title} icon={icon}>
      {text || label(key, lang)}
    </Tag>
  );
}

/** The lifecycle pill alone, or `null` when the record has no lifecycle status. */
function lifecyclePill(record, lang) {
  return record?.lifecycle_status ? statusTag(LIFECYCLE_TONE, record.lifecycle_status, lang) : null;
}

/** The "set manually" mark: an icon, where a second pill used to be. A word beside the stage made
    a two-line cell out of every manual row; the icon says the same in 16px, and spells it out in
    a tooltip that opens on hover AND on keyboard focus — so the wrapper is focusable, carries the
    name as its `aria-label`, and keeps a visible focus ring (`cp-manual` in `counterparties.scss`). */
function manualMark(lang) {
  const title = translate(COUNTERPARTIES_PAGE, "lc_manual_title", lang);
  return (
    <Tooltip
      trigger={["hover", "focus"]}
      title={
        <>
          <div className="cp-manual__tip-title">{title}</div>
          <div>{translate(COUNTERPARTIES_PAGE, "lc_manual_hint", lang)}</div>
        </>
      }>
      <span className="cp-manual" tabIndex={0} role="img" aria-label={title}>
        <UserSwitchOutlined />
      </span>
    </Tooltip>
  );
}

/** Lifecycle pill, plus the manual mark when a person — not automation — set the stage. `null`
    when the record has no lifecycle status, so a caller can fall back to a dash. */
export function lifecycleTags(record, lang) {
  if (!record?.lifecycle_status) return null;
  return (
    <Flex align="center" gap={4}>
      {lifecyclePill(record, lang)}
      {record.lifecycle_is_manual ? manualMark(lang) : null}
    </Flex>
  );
}
